import axios from 'axios';
import { app_version } from './Utils';
import moment from 'moment';

export const serverUrl = `${process.env.REACT_APP_SERVER_URL || ''}/api`;

axios.defaults.baseURL = serverUrl;
axios.defaults.headers.post['Content-Type'] = 'application/json';

const CUSTOMER_FIELDS = 'Id,Name,MobileNumber,Email,BillingAddress1,BillingAddress2,BillingCity,BillingPinCode,BillingStateName,BillingStateCode,AccountId';
const PRODUCT_FIELDS = 'Id,Name,Code,GroupName,ImageUrl,Price,UnitOfMeasureCode as Unit, UnitOfMeasureId, Discount, IgstTax.Percentage as TaxPercentage';
const DEFAULT_COMPANY_FILTERS = { departmentId: 0, companyId: 0 };

function setCompanyCodeHeader() {
    const companyCode = localStorage.getItem("Company-code");
    if (companyCode) {
        axios.defaults.headers.common['Company'] = companyCode;
    }
}

function saveCompanyCodeHeader(companyCode: string) {
    localStorage.setItem("Company-code", companyCode);
    axios.defaults.headers.common['Company'] = companyCode;
}

function gatewayError(method: string, error: unknown): never {
    console.error(`Error in ArthaGateway::${method} ->${error}`);
    throw error;
}

function withCompanyHeader(useCompanyHeader: boolean = true) {
    if (useCompanyHeader) {
        setCompanyCodeHeader();
    }
}

async function getData<T = any>(
    path: string,
    methodName: string,
    params?: Record<string, unknown>,
    useCompanyHeader: boolean = true
): Promise<T | undefined> {
    try {
        withCompanyHeader(useCompanyHeader);
        const response = params ? await axios.get(path, { params }) : await axios.get(path);
        return response?.data;
    } catch (error) {
        return gatewayError(methodName, error);
    }
}

async function postData<T = any>(
    path: string,
    payload: unknown,
    methodName: string,
    config?: Record<string, unknown>,
    useCompanyHeader: boolean = true
): Promise<T | undefined> {
    try {
        withCompanyHeader(useCompanyHeader);
        const response = config ? await axios.post(path, payload, config) : await axios.post(path, payload);
        return response?.data;
    } catch (error) {
        return gatewayError(methodName, error);
    }
}

function buildVoucherQuery(base: Record<string, unknown>, withPagination: boolean = true, pageNumber: number = 1, pageSize: number = 10) {
    return {
        ...base,
        ...DEFAULT_COMPANY_FILTERS,
        ...(withPagination ? { pageNumber, pageSize } : {})
    };
}

function getVoucherPdfLink(voucherType: 'sale' | 'order' | 'receipt' | 'creditNote', id: number, methodName: string) {
    return getData(`/voucher/${voucherType}/pdf/${id}`, methodName);
}

const ArthaGateway = {
    validateLogin: async function (companyCode: string, loginName: string, password: string) {
        const payload = { loginName, password };
        saveCompanyCodeHeader(companyCode);
        return postData('/user/validate', payload, 'validateLogin', undefined, false);
    },
    getCustomers: async function (name: string = "", pageNumber: number = 1, pageSize: number = 10, activeOnly: boolean = true) {
        const params = { name, pageNumber, pageSize, activeOnly, type: 'Customer', fields: CUSTOMER_FIELDS };
        return getData('/master/party', 'getCustomers', params);
    },
    createCustomer: async function (party: any) {
        return postData('/master/party', party, 'createCustomer');
    },
    getBalance: async function (accountId: number) {
        return getData(`/accounting/account/${accountId}/balance`, 'getBalance');
    },
    getTransactions: async function (accountId: number, durationId: string, pageNumber: number = 1, pageSize: number = 10) {
        const safePageNumber = pageNumber <= 0 ? 1 : pageNumber;
        const params = { durationId, accountId, pageNumber: safePageNumber, pageSize };
        return getData(`/accounting/account/${accountId}/transaction`, 'getTransactions', params);
    },
    getBusinessSummary: async function (partId: number, durationId: string) {
        return getData(`/master/party/businessSummary/${partId}`, 'getBusinessSummary', { durationId });
    },
    getSalesPersonBusinessSummary: async function (salesPersonId: number, durationId: string) {
        return getData('/master/salesPerson/businessSummary/', 'getSalesPersonBusinessSummary', { salesPersonId, durationId });
    },
    getSalesPersonOutstandingSummary: async function (salesPersonId: number, asOn: string) {
        return getData('/master/salesPerson/outstandingSummary/', 'getSalesPersonOutstandingSummary', { salesPersonId, asOn });
    },
    getPendingInvoices: async function (partyId: number) {
        return getData('/voucher/sale/pending', 'getPendingInvoices', { partyId });
    },
    getSalesperson: async function () {
        return getData('/master/salesPerson', 'getSalesperson', { activeOnly: true, reportsTo: -1 });
    },
    getSales: async function (partyId: number, durationId: string, paymentStatus: number, deliveryStatus: number, pageNumber: number = 1, pageSize: number = 10) {
        const params = buildVoucherQuery({ partyId, durationId, paymentType: 0, paymentStatus, deliveryStatus }, true, pageNumber, pageSize);
        return getData('/voucher/sale', 'getSales', params);
    },
    getSalesSummary: async function (partyId: number, durationId: string, paymentStatus: number, deliveryStatus: number) {
        const params = buildVoucherQuery({ partyId, durationId, paymentType: 0, paymentStatus, deliveryStatus }, false);
        return getData('/voucher/sale/summary', 'getSalesSummary', params);
    },
    getSale: async function (id: number) {
        return getData(`/voucher/sale/${id}`, 'getSale');
    },
    changeSaleStatus: async function (id: number, status: number, deliveredTo: string = "") {
        const params = status === 3
            ? { status, deliveredTo, deliveredOn: moment().format("YYYY-MM-DD hh:mm:ss") }
            : { status };
        return getData(`/voucher/sale/${id}/changeStatus`, 'changeSaleStatus', params);
    },
    getReceipts: async function (partyId: number, durationId: string, paymentType: number, accountId: number, pageNumber: number = 1, pageSize: number = 10) {
        const params = buildVoucherQuery({ partyId, durationId, paymentType, accountId }, true, pageNumber, pageSize);
        return getData('/voucher/receipt', 'getReceipts', params);
    },
    getReceiptsSummary: async function (partyId: number, durationId: string, paymentType: number, accountId: number) {
        const params = buildVoucherQuery({ partyId, durationId, accountId, paymentType }, false);
        return getData('/voucher/receipt/summary', 'getReceiptsSummary', params);
    },
    getCreditNotes: async function (partyId: number, durationId: string, salesPersonId: number, pageNumber: number = 1, pageSize: number = 10) {
        return getData('/voucher/creditNote', 'getCreditNotes', { partyId, durationId, salesPersonId, pageNumber, pageSize });
    },
    getCreditNotesSummary: async function (partyId: number, durationId: string, salesPersonId: number) {
        const params = buildVoucherQuery({ partyId, durationId, salesPersonId, aymentType: 0 }, false);
        return getData('/voucher/creditNote/summary', 'getCreditNotesSummary', params);
    },
    getSalesFileLink: async function (saleId: number) {
        return getVoucherPdfLink('sale', saleId, 'getSalesFileLink');
    },
    getSalesOrderFileLink: async function (salesOrderId: number) {
        return getVoucherPdfLink('order', salesOrderId, 'getSalesOrderFileLink');
    },
    getReceiptFileLink: async function (receiptId: number) {
        return getVoucherPdfLink('receipt', receiptId, 'getReceiptFileLink');
    },
    getCreditNoteFileLink: async function (creditNoteId: number) {
        return getVoucherPdfLink('creditNote', creditNoteId, 'getCreditNoteFileLink');
    },
    getDealerStatementFileLink: async function (accountId: number, durationId: string) {
        return getData(`/master/dealer/statement/pdf/${accountId}`, 'getDealerStatementFileLink', { durationId });
    },
    getOutstandingReportFileLink: async function (dealerId: number) {
        return getData(`/master/dealer/outstanding/pdf/${dealerId}`, 'getOutstandingReportFileLink');
    },
    getOrders: async function (partyId: number, durationId: string, salesPersonId: number, deliveryStatus: number, pageNumber: number = 1, pageSize: number = 10) {
        const params = buildVoucherQuery({ partyId, durationId, salesPersonId, deliveryStatus }, true, pageNumber, pageSize);
        return getData('/voucher/order', 'getOrders', params);
    },
    getOrdersSummary: async function (partyId: number, durationId: string, salesPersonId: number, deliveryStatus: number) {
        const params = buildVoucherQuery({ partyId, durationId, salesPersonId, deliveryStatus }, false);
        return getData('/voucher/order/summary', 'getOrdersSummary', params);
    },
    getCreditNoteEligibality: async function (partyId: number) {
        return getData('/reports/party/creditNoteEligibality', 'getCreditNoteEligibality', { partyId });
    },
    initiatePayment: async function (partyId: number, amount: number) {
        const redirectionUrl = window.location.origin + "/payment/result";
        return getData('/phonepe/initiate', 'initiatePayment', { partyId, amount, redirectionUrl });
    },
    processPayment: async function (tid: string, churn: string) {
        return getData('/phonepe/process', 'processPayment', { tid, churn });
    },
    getProductGroups: async function () {
        return getData('/master/product/group', 'getProductGroups');
    },
    getProductBrands: async function () {
        return getData('/master/product/brand', 'getProductBrands');
    },
    getProducts: async function (groupId: number, name: string, pageNumber: number = 1, pageSize: number = 20) {
        const params = {
            type: 1,
            activeOnly: true,
            name,
            groupId,
            pageNumber,
            pageSize,
            fields: PRODUCT_FIELDS
        };
        return getData('/master/product', 'getProducts', params);
    },
    getProductPricing: async function (partyId: number, productId: number) {
        return getData('master/party/product/pricing/', 'getProductPricing', { partyId, productId });
    },
    saveOrder: async function (order: any) {
        return postData('/voucher/salesOrder', order, 'saveOrder');
    },
    getSalesOrder: async function (partyId: number, durationId: string, salesPersonId: number, pageNumber: number = 1, pageSize: number = 10) {
        const params = buildVoucherQuery({ partyId, durationId, salesPersonId, reportsToId: salesPersonId }, true, pageNumber, pageSize);
        return getData('/voucher/order', 'getSalesOrder', params);
    },
    getSalesOrderSummary: async function (partyId: number, durationId: string, salesPersonId: number) {
        const params = buildVoucherQuery({ partyId, durationId, salesPersonId, aymentType: 0 }, false);
        return getData('/voucher/order/summary', 'getSalesOrderSummary', params);
    },
    uploadFile: async function (file: File) {
        const formData = new FormData();
        formData.append('file', file);
        return postData('/document/upload', formData, 'uploadFile', {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        }, false);
    },
    saveInvoice: async function (order: any) {
        return postData('/voucher/sale', order, 'saveInvoice');
    },
    saveReceipt: async function (receipt: any) {
        return postData('/voucher/receipt', receipt, 'saveReceipt');
    },
    getAccounts: async function (name: string, groupId: number = 0, pageNumber: number = 1, pageSize: number = 10) {
        const storageKey = `bankAccounts_${groupId}`;
        const cachedAccounts = sessionStorage.getItem(storageKey);
        if (cachedAccounts) {
            return JSON.parse(cachedAccounts);
        }

        const response = await getData('/accounting/account', 'getAccounts', { name, groupId, pageNumber, pageSize });
        if (response) {
            sessionStorage.setItem(storageKey, JSON.stringify(response));
        }
        return response;
    }
}

export default ArthaGateway;